import { ArcadeAvatar, ArcadeAvatarProps, ArcadeAvatarSize } from '@cash-design-system/react'
import { Color } from '@cash-web/protos/squareup/cash/ui/Color.pb'
import { UiAvatar, UiAvatarBadgeShape } from '@cash-web/protos/squareup/franklin/ui/ui_avatar.pb'

export const convertProtoColorToAvatarColor = (color?: Color) => {
  const light = color?.light?.srgb
  const dark = color?.dark?.srgb
  if (!light) {
    return undefined
  }
  return {
    light,
    dark: dark ?? light,
  }
}

const convertProtoImageToArcadeImage = (image: UiAvatar['image']) => {
  const light = image?.light_url
  const dark = image?.dark_url
  if (!light) {
    return undefined
  }
  return {
    lightUrl: light,
    darkUrl: dark ?? light,
  }
}

export const getArcadeAvatarPropsFromUiAvatarProto = ({ uiAvatarProto }: { uiAvatarProto: UiAvatar }) => {
  const arcadeAvatarProps: ArcadeAvatarProps = {}

  const {
    background_color,
    accessibility_value,
    image,
    monogram_text,
    icon,
    is_template_avatar,
    tint_color,
    border_color,
    badge_shape,
    badge_image,
    badge_icon,
  } = uiAvatarProto

  if (background_color) {
    arcadeAvatarProps['backgroundColor'] = convertProtoColorToAvatarColor(background_color)
  }

  if (accessibility_value) {
    arcadeAvatarProps['alt'] = accessibility_value
  }

  // Display priority: image > monogram_text > icon
  if (image) {
    arcadeAvatarProps['image'] = convertProtoImageToArcadeImage(image)
  } else if (monogram_text) {
    arcadeAvatarProps['initial'] = monogram_text
  } else {
    if (icon?.arcade_id) {
      arcadeAvatarProps['icon'] = {
        arcadeId: icon.arcade_id,
      }
    }
  }

  if (is_template_avatar) {
    arcadeAvatarProps['colorizeImage'] = true
  }

  if (tint_color) {
    arcadeAvatarProps['tintColor'] = convertProtoColorToAvatarColor(tint_color)
  }

  if (border_color) {
    arcadeAvatarProps['borderColor'] = convertProtoColorToAvatarColor(border_color)
  }

  // If the badge is a card, we need to stack the badge image/icon and the avatar
  if (badge_shape === UiAvatarBadgeShape.CARD) {
    const stackedAvatars: ArcadeAvatarProps[] = []
    const badgeImage = convertProtoImageToArcadeImage(badge_image)
    const badgeIcon = badge_icon?.arcade_id

    if (badgeIcon) {
      stackedAvatars.push({ icon: { arcadeId: badgeIcon } })
    } else if (badgeImage) {
      stackedAvatars.push({ image: badgeImage })
    }

    stackedAvatars.push(arcadeAvatarProps)

    return {
      stackedAvatars,
    }
  } else {
    if (badge_image) {
      arcadeAvatarProps['overlayImage'] = convertProtoImageToArcadeImage(badge_image)
    }

    if (badge_icon?.arcade_id) {
      arcadeAvatarProps['overlayIcon'] = {
        arcadeId: badge_icon.arcade_id,
      }
    }
  }

  return arcadeAvatarProps
}

/**
 * ArcadeAvatarFromUiAvatar is a component that converts a UiAvatar proto to ArcadeAvatar.
 *
 * If the badge_shape is CARD, it will return a stacked avatar with the badge image/icon and the avatar.
 */
export const ArcadeAvatarFromUiAvatar = ({
  uiAvatar = {},
  disabled,
  size,
  scaleFactor,
}: {
  uiAvatar?: UiAvatar
  disabled?: boolean
  size?: ArcadeAvatarSize
  scaleFactor?: number
}) => {
  const arcadeAvatarProps = getArcadeAvatarPropsFromUiAvatarProto({ uiAvatarProto: uiAvatar })
  return <ArcadeAvatar {...arcadeAvatarProps} disabled={disabled} size={size} scaleFactor={scaleFactor} />
}
